// src/pages/Admin/Users/RoleAssignmentModal.tsx
import React, { useState } from 'react';
import { X, Shield } from 'lucide-react';
import type { User } from '../../../types';

interface RoleAssignmentModalProps {
  user: User;
  currentUserRole: string;
  onAssign: (roles: string[]) => Promise<void>;
  onClose: () => void;
}

const RoleAssignmentModal: React.FC<RoleAssignmentModalProps> = ({
  user,
  currentUserRole,
  onAssign,
  onClose
}) => {
  const [selectedRoles, setSelectedRoles] = useState<string[]>(user.roles || []);
  const [loading, setLoading] = useState(false);

  const availableRoles = [
    { value: 'customer', label: 'Customer', color: 'gray' },
    { value: 'driver', label: 'Driver', color: 'green' },
    { value: 'warehouse', label: 'Warehouse Staff', color: 'orange' },
    { value: 'staff', label: 'Staff', color: 'blue' },
    { value: 'manager', label: 'Manager', color: 'purple' },
    ...(currentUserRole === 'admin' ? [{ value: 'admin', label: 'Admin', color: 'red' }] : [])
  ];

  const toggleRole = (role: string) => {
    setSelectedRoles(prev => 
      prev.includes(role)
        ? prev.filter(r => r !== role)
        : [...prev, role]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onAssign(selectedRoles);
    } catch (error) {
      console.error('Failed to assign roles:', error);
    } finally {
      setLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    const colors = {
      admin: 'red',
      manager: 'purple',
      staff: 'blue',
      driver: 'green',
      warehouse: 'orange',
      customer: 'gray'
    };
    return colors[role as keyof typeof colors] || 'gray';
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <span className="hidden sm:inline-block sm:align-middle sm:h-screen">&#8203;</span>

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <form onSubmit={handleSubmit}>
            <div className="px-6 py-4 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <div className="flex items-center">
                <Shield className="h-5 w-5 text-gray-500 mr-2" />
                <h3 className="text-lg font-medium text-gray-900">
                  Manage Roles - {user.full_name || `${user.first_name} ${user.last_name}`}
                </h3>
              </div>
              <button
                type="button"
                onClick={onClose}
                className="text-gray-400 hover:text-gray-500"
              >
                <X size={20} />
              </button>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-4">
                <p className="text-sm text-gray-600">
                  Select roles to assign to this user. Users can have multiple roles.
                </p>

                <div className="space-y-3">
                  {availableRoles.map(({ value, label, color }) => (
                    <label
                      key={value}
                      className={`flex items-center p-3 rounded-lg border-2 transition-colors cursor-pointer ${
                        selectedRoles.includes(value)
                          ? `border-${color}-500 bg-${color}-50`
                          : 'border-gray-200 hover:bg-gray-50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={selectedRoles.includes(value)}
                        onChange={() => toggleRole(value)}
                        className={`h-4 w-4 text-${color}-600 focus:ring-${color}-500 border-gray-300 rounded`}
                      />
                      <span className="ml-3">
                        <span className="text-sm font-medium text-gray-900">{label}</span>
                        <span className="text-xs text-gray-500 ml-2">({value})</span>
                      </span>
                    </label>
                  ))}
                </div>

                {selectedRoles.includes('admin') && currentUserRole !== 'admin' && (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <p className="text-sm text-yellow-800">
                      You don't have permission to assign admin role.
                    </p>
                  </div>
                )}
              </div>
            </div>

            <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Saving...' : 'Save Roles'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;